import { FC, useContext, useState } from 'react'

import StreamContext from 'shared/contexts/StreamContext'
import MicDevicePicker from 'shared/components/MicDevicePicker'
import { ReactComponent as IconMicrophoneSlash } from 'shared/assets/faMicrophoneSlash.svg'

type Props = {
  /**
   * Whether the user has in-app mic controls available (the control panel is
   * shown). When true we rely on the tile label plus the highlighted mic button
   * in the panel and skip the big banner (which would also overlap the panel).
   * When false there is no in-app way to fix it, so we surface an escalated
   * banner that can expand into a microphone picker for recovery.
   */
  canConfigure?: boolean
}

/**
 * "We can't hear you" indication. Reads mic health from StreamContext, so any
 * demo that renders it inside the StreamContext provider gets the warning for
 * free. Renders nothing while the mic is healthy or intentionally turned off.
 *
 * Place it inside the (relatively/absolutely positioned) user video tile
 * container — the tile label is absolutely positioned; the banner is fixed.
 */
const MicWarning: FC<Props> = ({ canConfigure = false }) => {
  const { micHealth, controls } = useContext(StreamContext)
  const [pickerOpen, setPickerOpen] = useState(false)

  const show = controls.mic && micHealth.status !== 'ok'
  if (!show) return null

  const title =
    micHealth.status === 'muted'
      ? 'Your microphone is muted'
      : "We can't hear you"

  const guidance =
    micHealth.status === 'muted'
      ? 'Unmute your microphone, check the input device, and make sure the browser has mic permission.'
      : 'Check that the correct microphone is selected and not muted in your system settings, and that the browser has mic permission.'

  return (
    <>
      {/* Compact label across the top of the user video tile (always shown) */}
      <div className='absolute top-0 left-0 right-0 z-20 flex items-center justify-center gap-1 rounded-t-md bg-red-500/90 px-1 py-0.5 text-[10px] leading-tight text-white animate-pulse'>
        <IconMicrophoneSlash className='h-2.5 w-2.5 shrink-0 fill-current' />
        <span>{title}</span>
      </div>

      {/* Escalated banner — only when there are no in-app controls to fix it.
          Tapping it expands a microphone picker so the user can still switch
          their input device even though the control panel is hidden. */}
      {!canConfigure && micHealth.escalated && (
        <div className='fixed bottom-6 left-1/2 z-50 w-[min(90vw,22rem)] -translate-x-1/2 rounded-lg bg-red-600 text-white shadow-lg'>
          <button
            type='button'
            onClick={() => setPickerOpen((o) => !o)}
            className='flex w-full items-start gap-3 px-4 py-3 text-left'
          >
            <IconMicrophoneSlash className='mt-0.5 h-5 w-5 shrink-0 fill-current' />
            <div className='text-sm'>
              <p className='font-semibold'>{title}</p>
              <p className='opacity-90'>{guidance}</p>
              <p className='mt-1 text-xs underline'>
                {pickerOpen ? 'Hide microphones' : 'Tap to switch microphone'}
              </p>
            </div>
          </button>
          {pickerOpen && (
            <div className='max-h-48 overflow-auto border-t border-white/30 px-4 py-3'>
              <MicDevicePicker />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default MicWarning
